import { Box, CircularProgress, Typography } from "@mui/material";
import { Line } from "react-chartjs-2";
import "helpers/chartjs";
import { useStrategyQuery } from "hooks/useStrategyQuery";
import { useAccountQuery } from "hooks/useAccountQuery";
import { useGetReportQuery } from "../../../features/trading/accounts";
import { useReportTrigger } from "../../../containers/ReportPageDeprecated";
import { useMemo } from "react";

const PerformanceChart = () => {
  const { data: stockData } = useStrategyQuery();
  const { data: accountData } = useAccountQuery();
  const { shouldFetch, columnPeriods } = useReportTrigger();

  // Create query parameters - use the same periods as IncomeSummary
  const queryParams = useMemo(() => {
    if (
      stockData?.id &&
      accountData?.id &&
      shouldFetch &&
      columnPeriods.length > 0
    ) {
      return {
        account_ids: [accountData.id],
        strategy_ids: [stockData.id],
        periods: columnPeriods.map((col) => ({
          name: col.name,
          period: col.period,
        })),
      };
    }
    return null;
  }, [stockData?.id, accountData?.id, columnPeriods, shouldFetch]);

  // Use the query hook
  const {
    data: reportData,
    isLoading,
    error,
  } = useGetReportQuery(queryParams!, {
    skip: !queryParams,
  });

  // Memoize the chart data
  const chartData = useMemo(() => {
    if (reportData?.data?.graph_data && reportData.data.graph_data.length > 0) {
      const graphData = reportData.data.graph_data;

      // Create a copy of the array before sorting to avoid modifying the read-only original
      const sortedData = [...graphData].sort(
        (a: any, b: any) =>
          new Date(a.date).getTime() - new Date(b.date).getTime(),
      );

      return {
        // The dates as the strings the backend sent, formatted for display
        // only when a tick is drawn. `new Date("2025-08-28")` is read as UTC
        // midnight, which renders as the previous day anywhere west of
        // Greenwich, so a Date here would move every point back a day.
        dates: sortedData.map((item: any) => String(item.date)),
        stockValues: sortedData.map((item: any) => item.stock_value),
        optionValues: sortedData.map((item: any) => item.option_value),
      };
    }
    return null;
  }, [reportData?.data?.graph_data]);

  if (isLoading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 400,
          mt: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 400,
          mt: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography color="error">
          Error loading chart data. Please try again.
        </Typography>
      </Box>
    );
  }

  if (!chartData) {
    return (
      <Box
        sx={{
          width: "100%",
          height: 400,
          mt: 2,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <Typography color="text.secondary">
          No chart data available. Click "Get" to load data.
        </Typography>
      </Box>
    );
  }

  const name = stockData?.name || "Stock";

  return (
    <Box sx={{ width: "100%", height: 400, mt: 2 }}>
      {/*
        Chart.js sizes itself to its parent, so the height is the box's and
        `maintainAspectRatio: false` is what stops the library holding a 2:1
        box of its own instead.
      */}
      <Box sx={{ position: "relative", width: "100%", height: 300 }}>
        <Line
          data={{
            labels: chartData.dates,
            datasets: [
              {
                label: `${name} Optic`,
                data: chartData.optionValues,
                borderColor: "#E4C6B2",
                backgroundColor: "#E4C6B2",
                borderWidth: 2,
                pointRadius: 0,
                pointHitRadius: 12,
              },
              {
                label: `${name} Stock`,
                data: chartData.stockValues,
                borderColor: "#645028",
                backgroundColor: "#645028",
                borderWidth: 2,
                pointRadius: 0,
                pointHitRadius: 12,
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: "index", intersect: false },
            plugins: { legend: { position: "bottom" } },
            scales: {
              // A category axis over the date strings, formatted per tick.
              // See chartData above for why these are not Date objects.
              x: {
                type: "category",
                grid: { display: false },
                ticks: { maxRotation: 0, autoSkip: true },
              },
              y: { type: "linear" },
            },
          }}
        />
      </Box>
    </Box>
  );
};

export default PerformanceChart;
